
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { PERMISSION } from '@/permissions';
import { Validate, Provider } from '@/types';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import ScrollableButtons from '@/components/shared/ScrollableButtons.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import SelectComponent from '@/components/shared/Select.vue';

interface OrderAdditionalField {
	label: string;
	type: string;
	name: string;
	optional?: boolean;
	options?: any[];
	disabled?: boolean;
	onChange?: (value: any) => void;
}

const namespace = 'orders';

@Component<OrderDetailsModal>({
	components: {
		TextField,
		StandardModal,
		ValidationObserver,
		ValidationProvider,
		ScrollableButtons,
		TextButton,
		SelectComponent
	}
})
export default class OrderDetailsModal extends Vue {
	@Action('setOrderDetails', { namespace }) setOrderDetails!: (orderDetails: OrderDetails) => void;
	@Action('resetOrderDetails', { namespace }) resetOrderDetails!: () => void;
	@Action('setCosts', { namespace }) setCosts!: () => void;
	@Action('fireEvent', { namespace }) fireEvent!: (payload: FireEventPayload) => void;
	@Action('fetchLatestBeoNumber', { namespace }) getLatestBeoNumber!: (payload: GetLatestBeoPayload) => Promise<string | undefined>;
	@Action('resetCustomFields', { namespace: 'customQuestions' }) private resetCustomFields!: () => void;
	@Getter('getOrderDetails', { namespace }) orderDetails!: OrderDetails;
	@Getter('getCartItems', { namespace }) cartItems!: OrderItem[];
	@Getter('hasPermission', { namespace: 'auth' }) hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getPublishedMenus', { namespace: 'menus' }) publishedMenus!: Menu[];
	@Getter('getTenantId', { namespace: 'auth' }) tenantId!: string;
	@Getter('isQuoting', { namespace }) isQuoting!: boolean;
	@Getter('getEmployeeId', { namespace: 'auth' }) employeeId!: string | null;
	@Getter('areCateringEventsEnabled', { namespace: 'auth' }) areCateringEventsEnabled!: boolean;
	@Prop({ type: String, required: true }) private modalType!: string;
	@Prop({ type: String, required: false }) private returnPath!: string;
	@Prop({ type: Array, required: true }) private orderTypeOptions!: { text: string, value: 'dine-in' | 'take-out' | 'generic-catering' }[];
	@Prop({ type: Boolean, default: false }) private isTableInfoFetchedByApp!: boolean;
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;

	$refs!: {
		observer: Validate;
		menuManagerEventPackage: Provider[];
		tableLocation: Provider[];
		tableArea: Provider[];
		takeoutType: Provider[];
		additionalButtonGroup: HTMLDivElement;
	};

	private genericTableLocationsOptions: any = [];
	private mutableOrderDetails: OrderDetails = {
		type: '',
		tableArea: '',
		tableLocation: '',
		tableNum: '',
		takeoutType: '',
		checkNum: ''
	};
	private chosenOrderType: 'dine-in' | 'lane-res' | 'take-out' | 'generic-catering' | 'events' | '' = '';
	private isFetchingBeo: boolean = false;

	/**
	 * Get additional required fields based on the order type
	 *
	 * @return {OrderAdditionalField[]}
	 */
	private get orderTypeAdditionalFields(): OrderAdditionalField[] {
		if (this.mutableOrderDetails.type === 'dine-in') {
			return this.getDineInAdditionalFields(this.mutableOrderDetails.menuManagerEvent);
		}
		else if (this.mutableOrderDetails.type === 'take-out' || this.mutableOrderDetails.type === 'generic-catering') {
			return this.getTakeOutAdditionalFields();
		}
		return [];
	}

	private get hideCancelButton(): boolean {
		return this.modalType === 'create' && this.hasPermission(PERMISSION.SESSION_STAFF) && !window.B2BApp;
	}

	private get cancelButton(): { text: string, color: string, icon?: string }{
		if (window.B2BApp && this.modalType === 'create') {
			return {
				text: this.$t('utils.navbar.btn_back_to_app'),
				icon: 'mdi-logout',
				color: 'white'
			};
		}
		else if (this.modalType === 'create') {
			return {
				text: this.$t('shared.cancel'),
				color: 'white'
			};
		}
		return {
			text: this.$t('orders.details.delete_btn'),
			color: 'primary'
		};
	}

	private created(): void {
		// If we're coming from the app, this means we're displaying the modal for event details
		// and this require us to set the generic table locations.
		if (this.modalType === 'edit' || this.isTableInfoFetchedByApp) {
			// We need to set the generic table locations for the select field before editing
			// or else the select field will not have the correct options and the value will not be displayed
			this.setGenericTableLocations(this.orderDetails.tableLocation);
		}
		this.mutableOrderDetails = { ...this.orderDetails };
		this.initializeOrderType();
	}

	/**
	 * Set the order type if there is only one option
	 * FOR BOWLERO ONLY - Hard set to dine-in no matter what other types there is.
	 * (Bowlero menus will always have dine-in, thus dine-in will be the first option at all times)
	 *
	 * @return {void}
	 */
	private initializeOrderType(): void {
		if (this.modalType === 'edit' || this.isTableInfoFetchedByApp) {
			if (this.orderDetails.type === 'generic-catering' || this.orderDetails.type === 'take-out') {
				this.chosenOrderType = this.orderDetails.type;
			}
			// BOWLERO ONLY - Could be used for other clients
			else if(this.orderDetails.menuManagerEvent) {
				this.chosenOrderType = 'events';
			}
			// BOWLERO ONLY
			else if(this.orderDetails.menuManagerLaneReservation) {
				this.chosenOrderType = 'lane-res';
			}
			else {
				this.chosenOrderType = 'dine-in';
			}
			this.selectOrderType();
		}
		// New order, set the order type if there is only one option
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		else if (this.orderTypeOptions.length === 1 || this.restaurant.group === 'bowlero' || this.restaurant.groups?.includes('bowlero')) {
			this.chosenOrderType = this.orderTypeOptions[0].value;
			this.mutableOrderDetails.type = this.orderTypeOptions[0].value;
		}
		// If coming from an inquiry request set catering event as default selection
		else if (this.isQuoting) {
			this.chosenOrderType = 'generic-catering';
			this.mutableOrderDetails.type = 'generic-catering';
			this.mutableOrderDetails.takeoutType = 'catering-event';
		}
	}

	/**
	 * Select order type and reset all additional fields when the order type changes
	 * The reason we can't use v-model here is because the order type does not neccessarily mean
	 * we're updating the actual order object with the type. A good example to look at is lane-res.
	 * Lane-res is a dine-in order type with a flag for lane-res.
	 *
	 * @return {void}
	 */
	private selectOrderType(): void {
		// Don't want to keep the lane reservation / event flags in the order object if it's not a lane reservation
		Object.assign(this.mutableOrderDetails, {
			menuManagerLaneReservation: undefined,
			menuManagerEvent: undefined,
			menuManagerEventBEO: undefined,
			menuManagerEventPackage: undefined
		});

		switch (this.chosenOrderType) {
			case 'generic-catering':
				this.mutableOrderDetails.type = 'generic-catering';
				break;
			case 'take-out':
				this.mutableOrderDetails.type = 'take-out';
				break;
			case 'events': // FOR BOWLERO ONLY - Could be used for other clients
				this.mutableOrderDetails.type = 'dine-in';
				this.mutableOrderDetails.menuManagerEvent = true;
				this.mutableOrderDetails.menuManagerEventBEO = this.isTableInfoFetchedByApp ? this.orderDetails.menuManagerEventBEO : '';
				this.mutableOrderDetails.menuManagerEventPackage = this.isTableInfoFetchedByApp ? this.orderDetails.menuManagerEventPackage : '';
				break;
			case 'lane-res': // FOR BOWLERO ONLY
				this.mutableOrderDetails.type = 'dine-in';
				this.mutableOrderDetails.menuManagerLaneReservation = true;
				break;
			default:
				this.mutableOrderDetails.type = 'dine-in';
				break;
		}

		this.mutableOrderDetails = {
			...this.mutableOrderDetails,
			tableArea: this.isTableInfoFetchedByApp ? this.mutableOrderDetails.tableArea : '',
			tableLocation: this.isTableInfoFetchedByApp ? this.mutableOrderDetails.tableLocation : '',
			tableNum: this.isTableInfoFetchedByApp ? this.mutableOrderDetails.tableNum : '',
			checkNum: this.isTableInfoFetchedByApp ? this.mutableOrderDetails.checkNum : ''
		};

		this.resetButtonGroupsScroll();
	}

	/**
	 * Get the additional fields for dine-in orders
	 * Either generic table locations or a table number input
	 * If event is true, we show event packages
	 *
	 * @param {boolean | undefined} event
	 * @return {OrderAdditionalField[]}
	 */
	private getDineInAdditionalFields(event: boolean | undefined): OrderAdditionalField[] {
		let orderAdditionalFields: OrderAdditionalField[] = [];

		// If the order type is event, we only need to show the BEO field.
		// Event menus are now displayed on the order page.
		if (event) {
			orderAdditionalFields.push(
				{
					label: this.$t('orders.details.event_beo_label'),
					type: 'input',
					name: 'menuManagerEventBEO',
					disabled: this.isTableInfoFetchedByApp && !!this.orderDetails.menuManagerEventBEO,
					optional: true,
					onChange: (value: string) => {
						this.mutableOrderDetails.menuManagerEventBEO = value;
					}
				}
			);
		}

		// Generic table locations
		if (this.restaurant.generic_table_locations?.custom) {
			const tableLocations: string[] = this.restaurant.generic_table_locations.custom.map((table: CustomGenericTableLocations) => table.location);
			// Set the table location if there is only one option
			if (tableLocations.length === 1) {
				this.mutableOrderDetails.tableLocation = tableLocations[0];
				this.setGenericTableLocations(tableLocations[0]);
			}
			return [
				...orderAdditionalFields,
				{
					label: this.$t('orders.details.seating_area_label'),
					type: 'select',
					name: 'tableLocation',
					options: tableLocations,
					disabled: this.isTableInfoFetchedByApp,
					onChange: (value: string) => {
						Vue.set(this.mutableOrderDetails, 'tableLocation', value);
						Vue.set(this.mutableOrderDetails, 'tableNum', '');
						Vue.set(this.mutableOrderDetails, 'tableArea', '');
						Vue.set(this.mutableOrderDetails, 'checkNum', '');
						this.setGenericTableLocations(value);
					}
				},
				{
					label: this.$t('orders.details.seating_details_label'),
					type: 'select',
					name: 'tableArea',
					options: this.genericTableLocationsOptions,
					disabled: !this.genericTableLocationsOptions.length || this.isTableInfoFetchedByApp,
					onChange: (value: string) => {
						Vue.set(this.mutableOrderDetails, 'tableArea', value);
						Vue.set(this.mutableOrderDetails, 'checkNum', '');
						// Find the table number based on the selected area
						const location = this.restaurant.generic_table_locations.custom!.find((table: CustomGenericTableLocations) => table.location === this.mutableOrderDetails.tableLocation);
						const tableNum = location!.areas.split(',').find((area: string) => area.includes(value))!.split(':')[1].trim();
						Vue.set(this.mutableOrderDetails, 'tableNum', tableNum);
						this.$refs.tableArea[0].syncValue(value);

						this.fetchAndPopulateLatestBeo();
					}
				}
			];
		}

		// Classic table number
		return [
			...orderAdditionalFields,
			{
				label: this.$t('orders.details.table_number_label'),
				name: 'tableNum',
				type: 'input',
				disabled: this.isTableInfoFetchedByApp,
				onChange: (value: string) => {
					this.mutableOrderDetails.tableNum = value;
					this.fetchAndPopulateLatestBeo();
				}
			}
		];
	}

	/**
	 * Set the generic table locations for the select field from restaurant settings
	 *
	 * @param {string} locationName
	 * @return {void}
	 */
	private setGenericTableLocations(locationName: string): void {
		if (!locationName) {
			return;
		}
		const location = this.restaurant.generic_table_locations?.custom!.find((table: CustomGenericTableLocations) => table.location === locationName);
		const areas: string[] = [];
		if (location && location.areas?.length) {
			location.areas.split(',').reduce((object: any, value: string) => {
				let strParts = value.split(':');
				if(strParts[0] && strParts[1]) {
					areas.push(strParts[0].trim());
				}
				return object;
			}, {});
		}
		this.genericTableLocationsOptions = areas;
	}

	/**
	 * Get the additional fields for take-out orders
	 *
	 * @return {OrderAdditionalField[]}
	 */
	private getTakeOutAdditionalFields(): OrderAdditionalField[] {
		const takeoutTypes = [];
		// Show pickup option if the order type is not catering or if the pickup option is enabled for catering orders
		if (this.mutableOrderDetails.type !== 'generic-catering' || (this.mutableOrderDetails.type === 'generic-catering' && this.restaurant.order_options?.generic_catering_show_pickup_option)) {
			takeoutTypes.push({
				text: this.$t('orders.details.pickup'),
				value: 'pickup'
			});
		}

		// Need to check the object keys here because if delivery was setup at some point then disabled
		// There will still be a delivery object, but it will be empty
		if (this.restaurant.delivery && Object.keys(this.restaurant.delivery).length) {
			takeoutTypes.push({
				text: this.$t('orders.details.delivery'),
				value: 'delivery'
			});
		}

		if (this.areCateringEventsEnabled) {
			takeoutTypes.push({
				text: this.$t('orders.management.parent_card.header.event_label'),
				value: 'catering-event'
			});
		}

		// Set the takeout type if there is only one option
		if (takeoutTypes.length === 1) {
			this.mutableOrderDetails.takeoutType = takeoutTypes[0].value;
		}

		return [
			{
				label: this.$t('orders.details.takeout_type_label'),
				type: 'select',
				name: 'takeoutType',
				options: takeoutTypes,
				onChange: (value: string) => Vue.set(this.mutableOrderDetails, 'takeoutType', value)
			}
		];
	}

	/**
	 * Reset the scroll position of all the additional fields that are button groups
	 *
	 * @return {void}
	 */
	private resetButtonGroupsScroll(): void {
		const buttonGroups = document.getElementsByClassName('additional-fields-button-group');
		if (buttonGroups.length) {
			for (let i = 0; i < buttonGroups.length; i++) {
				buttonGroups[i].scrollLeft = 0;
			}
		}
	}

	/**
	 * Handles what happens when the modal cancel button is clicked
	 * If editing details, reset the order details modal
	 * If user is on the app, go back to the app
	 * If creating a new order and user isn't a staff role, reset and redirect to order management
	 *
	 * @return {void}
	 */
	private handleModalCancel(): void {
		if (this.modalType === 'edit') {
			this.mutableOrderDetails = {
				type: '',
				tableArea: '',
				tableLocation: '',
				tableNum: '',
				takeoutType: '',
				checkNum: '',
				menuManagerEvent: undefined,
				menuManagerEventBEO: undefined,
				menuManagerEventPackage: undefined,
				menuManagerLaneReservation: undefined
			};
			this.$emit('delete-order');
			this.initializeOrderType();
		}
		else if (window.B2BApp) {
			window.B2BApp.backToApp();
		}
		else if (this.hasPermission(PERMISSION.OPERATOR)) {
			this.resetOrderDetails();
			this.$router.push(this.returnPath || '/order-management').catch(() => {});
		}
	}

	/**
	 * Validate the form, then set the order details and close the modal
	 *
	 * @return {Promise<void>}
	 */
	private async handleModalConfirm(): Promise<void> {
		const isValid = await this.$refs.observer.validate();

		if (isValid) {
			let updateCosts = false;

			// If there are items in the cart and the takeout type has changed, update the costs
			if (this.cartItems && this.cartItems.length && this.orderDetails.type === 'take-out' && this.mutableOrderDetails.takeoutType !== this.orderDetails.takeoutType) {
				updateCosts = true;
			}
			// Reset custom fields for catering orders if takeout type has changed
			if (this.modalType === 'edit' && this.orderDetails.type === 'generic-catering' && this.mutableOrderDetails.takeoutType !== this.orderDetails.takeoutType) {
				this.resetCustomFields();
			}
			this.setOrderDetails(this.mutableOrderDetails);

			if (updateCosts) {
				await this.setCosts();
			}
			this.$emit('confirm');
		}
	}

	/**
	 * Handle the Fire button click for Event BEO
	 * Validates the form first, same as Continue button
	 *
	 * @return {Promise<void>}
	 */
	private async handleFireEvent(): Promise<void> {
		const isValid = await this.$refs.observer.validate();

		if (isValid) {
			const payload: FireEventPayload = {
				restaurantId: this.tenantId,
				beoNumber: this.mutableOrderDetails.menuManagerEventBEO || '',
				tableArea: this.mutableOrderDetails.tableArea,
				tableLocation: this.mutableOrderDetails.tableLocation,
				tableNum: this.mutableOrderDetails.tableNum,
				employeeId: this.employeeId || undefined
			};

			try {
				await this.fireEvent(payload);
				Toast.success(this.$t('orders.details.fire_event_success'));
				this.handleSuccessfulFireEvent();
			}
			catch (error) {
				Toast.error(error == 'Conflict' ? this.$t('orders.details.fire_event_duplicate_error') : this.$t('orders.details.fire_event_error'));
			}
		}
	}

	/**
	 * Fetch and populate the latest BEO number for the selected table
	 *
	 * @return {Promise<void>}
	 */
	private async fetchAndPopulateLatestBeo(): Promise<void> {
		const { tableArea, tableLocation, tableNum } = this.mutableOrderDetails;

		// Only fetch if we have complete table information and BEO input should be visible
		if (tableArea && tableLocation && tableNum && this.shouldShowBeoInput()) {
			this.isFetchingBeo = true;
			try {
				const latestBeoNumber = await this.getLatestBeoNumber({
					tableArea,
					tableLocation,
					tableNum
				});

				// Populate the BEO input with the fetched value if available and current value is empty
				if (latestBeoNumber && !this.mutableOrderDetails.menuManagerEventBEO) {
					Vue.set(this.mutableOrderDetails, 'menuManagerEventBEO', String(latestBeoNumber));
				}
			}
			catch (error) {
				// Silently handle error - BEO fetching should not stop ordering.
			}
			finally {
				this.isFetchingBeo = false;
			}
		}
	}

	/**
	 * Check if BEO input should be visible based on current order type
	 *
	 * @return {boolean}
	 */
	private shouldShowBeoInput(): boolean {
		return this.mutableOrderDetails.type === 'dine-in' && this.mutableOrderDetails.menuManagerEvent === true;
	}

	/**
	 * Handle successful fire event - dismiss modal or return to app
	 *
	 * @return {void}
	 */
	private handleSuccessfulFireEvent(): void {
		// If we're in a B2B app context, return to the tab list page.
		if (window.B2BApp) {
			setTimeout(() => {
				window.B2BApp.backToApp();
			}, 1500); // Allow time for the user to see the success message
		}
	}

	/**
	 * Close the modal if editing order details, otherwise cancel (reset and redirect)
	 * If the user is staff do nothing, this prevents the modal from closing when clicking outside the modal
	 * Staff shouldn't be able to close the modal without filling out the form
	 *
	 * @return {void}
	 */
	private handleModalClose(): void {
		if (this.modalType === 'edit') {
			this.$emit('close');
		}
		else if (this.hasPermission(PERMISSION.OPERATOR)) {
			this.handleModalCancel();
		}
		return;
	}
}
